import { auth } from '../firebase';
import { attestModeration, submitModerationAppeal } from './secureFunctions';
import { deleteMediaObject, requestUploadUrl, uploadFileToSignedUrl } from './mediaApi';
import { compressImage } from './imageCompression';

const FALLBACK_TTL_MS = 15 * 60 * 1000;

/**
 * Shrinks large photos before upload by re-encoding them as high-quality WebP at their original
 * width and height (no downscaling). Documents are left untouched for legibility, and files already
 * compressed by a page (`*_cmp.*`) are not compressed twice.
 */
async function optimizeForUpload(file: File | Blob, folder: string): Promise<File | Blob> {
  if (!(file instanceof File)) return file;
  if (/verification|document/i.test(folder)) return file;
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type) || /_cmp\.\w+$/.test(file.name) || file.size < 300 * 1024) return file;
  return compressImage(file, { maxWidth: Infinity, maxHeight: Infinity, quality: 0.86, mimeType: 'image/webp' });
}

export interface ModeratedUploadOptions {
  folder: string;
  field: string;
  file: File | Blob;
  filename?: string;
  onProgress?: (percent: number) => void;
  metadata?: Record<string, string>;
  contentType?: string;
}

export interface ModerationProof {
  token: string;
  nonce: string;
  storagePath: string;
  field: string;
  nsfwScore: number;
  deviceCompatible: boolean;
  issuedAt: number;
  expiresAt: number;
  claims?: Record<string, unknown>;
}

export interface ModeratedUploadResult {
  objectKey: string;
  storagePath: string;
  moderationToken: string;
  nonce: string;
  field: string;
  proof: ModerationProof;
}

export type ModerationTokenMap = Record<string, ModerationProof>;

function sanitizeSegment(segment: string) {
  return segment.replace(/[^a-zA-Z0-9-_]/g, '-');
}

function deriveFilename(file: File | Blob, requested?: string) {
  if (requested) return sanitizeSegment(requested.trim());
  const base = (file as File).name || `blob-${Date.now()}`;
  const dotIndex = base.lastIndexOf('.');
  const stem = dotIndex >= 0 ? base.slice(0, dotIndex) : base;
  const ext = dotIndex >= 0 ? base.slice(dotIndex + 1) : 'bin';
  const safeStem = sanitizeSegment(stem || 'media');
  const safeExt = sanitizeSegment(ext || 'bin');
  return `${safeStem}-${Date.now()}.${safeExt}`;
}

function collectTextSamples(metadata?: Record<string, string>) {
  if (!metadata) return [] as string[];
  const candidates = ['caption', 'title', 'description', 'tags', 'text'];
  const values = candidates
    .map((key) => metadata[key])
    .filter((value): value is string => Boolean(value));
  return values.map((value) => value.toLowerCase());
}

function normalizeExpiryMillis(raw?: number) {
  if (!Number.isFinite(raw) || !raw) {
    return Date.now() + FALLBACK_TTL_MS;
  }
  const value = Number(raw);
  if (value > 1_000_000_000_000) {
    return value;
  }
  if (value <= 0) {
    return Date.now() + FALLBACK_TTL_MS;
  }
  return value * 1000;
}

async function offerModerationAppeal(objectKey: string, error: unknown) {
  try {
    const wantsReview = window.confirm(
      'This media appears to break Belivah\'s community guidelines (nudity or sexual content) and was not published.\n\nIf you believe this is a mistake, press OK to ask a moderator to review it.',
    );
    if (!wantsReview) return;
    const reason = window.prompt('Briefly explain why this media is appropriate (at least 5 characters):', '');
    if (!reason || reason.trim().length < 5) return;
    await submitModerationAppeal({ contentType: 'upload', contentId: objectKey, reason: reason.trim() });
    (error as any).appealSubmitted = true;
    window.alert('Thanks. A moderator will review your request. If approved, you can upload the same file again.');
  } catch (appealError) {
    console.warn('moderation-appeal-failed', appealError);
  }
}

export async function uploadModeratedMedia(options: ModeratedUploadOptions): Promise<ModeratedUploadResult> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('auth-required');
  }

  const folder = sanitizeSegment(options.folder || 'misc');
  const file = await optimizeForUpload(options.file, options.folder || '');
  const optimized = file !== options.file;
  const filename = deriveFilename(file, optimized ? undefined : options.filename);
  const requestedName = `${folder}-${filename}`;
  const textSamples = collectTextSamples(options.metadata);

  const contentType = (optimized ? file.type : options.contentType) || (file as File).type || 'application/octet-stream';
  const signed = await requestUploadUrl({
    filename: requestedName,
    contentType,
  });

  const objectKey = signed.objectKey;
  const storagePath = objectKey;

  // Upload first (VPS-only moderation needs the object to exist).
  await uploadFileToSignedUrl({
    uploadUrl: signed.uploadUrl,
    file,
    headers: signed.requiredHeaders,
    onProgress: options.onProgress,
  });

  let response: any;
  try {
    response = await attestModeration({
      contentPath: objectKey,
      contentType,
      textSamples,
    });
  } catch (e) {
    if (String((e as any)?.code || '').toUpperCase() === 'NSFW_BLOCKED') {
      // Keep the object so a moderator can review an appeal; it is never referenced publicly.
      await offerModerationAppeal(objectKey, e);
      throw e;
    }
    // Best-effort cleanup: if server rejects the content, delete the uploaded object.
    try {
      await deleteMediaObject(objectKey);
    } catch {
      // ignore
    }
    throw e;
  }

  const moderationToken = response.moderationToken as string;
  const nonce = response.nonce as string;
  if (!moderationToken) {
    throw new Error('moderation-token-missing');
  }

  const claims = (response.claims || {}) as Record<string, unknown>;
  const claimExpiry = typeof claims.exp === 'number' ? claims.exp : undefined;
  const expiresAt = normalizeExpiryMillis(typeof response.expiresAt === 'number' ? response.expiresAt : claimExpiry);

  const claimedScore = typeof claims.nsfwScore === 'number' ? Number(claims.nsfwScore) : 0;

  const proof: ModerationProof = {
    token: moderationToken,
    nonce,
    storagePath,
    field: options.field,
    nsfwScore: Number(clampScore(claimedScore).toFixed(4)),
    deviceCompatible: true,
    issuedAt: Date.now(),
    expiresAt,
    claims,
  };

  return { objectKey, storagePath, moderationToken, nonce, field: options.field, proof };
}

function clampScore(x: number) {
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

export function mergeModerationTokens(
  existing: ModerationTokenMap | undefined,
  upload: ModeratedUploadResult,
  fieldOverride?: string
): ModerationTokenMap {
  const next: ModerationTokenMap = { ...(existing || {}) };
  next[fieldOverride || upload.field] = upload.proof;
  return next;
}

export function deriveNsfwScores(tokens?: ModerationTokenMap | null) {
  if (!tokens) return null;
  const summary: Record<string, number> = {};
  for (const [field, proof] of Object.entries(tokens)) {
    if (!proof) continue;
    summary[field] = Number(proof.nsfwScore.toFixed(4));
  }
  return Object.keys(summary).length ? summary : null;
}

export function normalizeModerationTokens(input: unknown): ModerationTokenMap | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const next: ModerationTokenMap = {};
  for (const [field, value] of Object.entries(input as Record<string, unknown>)) {
    const proof = legacyProofFromValue(field, value);
    if (proof) next[field] = proof;
  }
  return Object.keys(next).length ? next : undefined;
}

function legacyProofFromValue(field: string, value: unknown): ModerationProof | null {
  if (!value) return null;
  if (typeof value === 'string') {
    return createProofFromString(value, field);
  }
  if (typeof value === 'object') {
    return createProofFromObject(field, value as Record<string, unknown>);
  }
  return null;
}

function createProofFromString(token: string, field: string): ModerationProof {
  const now = Date.now();
  return {
    token,
    nonce: token,
    storagePath: '',
    field,
    nsfwScore: 0,
    deviceCompatible: true,
    issuedAt: now,
    expiresAt: now + FALLBACK_TTL_MS,
  };
}

function createProofFromObject(field: string, value: Record<string, unknown>): ModerationProof | null {
  const token = firstString(value.token, value.moderationToken, value.value, value.nonce);
  if (!token) return null;
  const now = Date.now();
  const nsfwScore = typeof value.nsfwScore === 'number'
    ? value.nsfwScore
    : Number(value.score ?? value.nsfw ?? 0) || 0;
  const expiresCandidate = typeof value.expiresAt === 'number'
    ? value.expiresAt
    : typeof value.exp === 'number'
      ? value.exp
      : undefined;
  return {
    token,
    nonce: firstString(value.nonce, token) || token,
    storagePath: firstString(value.storagePath, value.path, value.contentPath) || '',
    field,
    nsfwScore,
    deviceCompatible: value.deviceCompatible !== false,
    issuedAt: typeof value.issuedAt === 'number' ? value.issuedAt : now,
    expiresAt: normalizeExpiryMillis(expiresCandidate),
    claims: typeof value.claims === 'object' ? (value.claims as Record<string, unknown>) : undefined,
  };
}

function firstString(...candidates: Array<unknown>) {
  for (const entry of candidates) {
    if (typeof entry === 'string' && entry.trim().length > 0) {
      return entry;
    }
  }
  return '';
}
